<script setup lang="ts">
/**
 * Supporting-document uploader for the incorporation E-Filing screen.
 *
 * - Shows already-uploaded documents (from setup-entity, by fileCd) as view links.
 * - Lets the user attach fresh PDFs (held as base64, uploaded server-side at submit).
 * - Lets the user REPLACE an existing document with a new PDF (the old fileCd is
 *   recorded on the replacement so the server can drop it).
 * Mutates the passed `docs` array in place.
 */
import { ref } from 'vue';
import { makeDocRefFromFile, validateEfUploadFile } from '../composables/fileUpload';
import type { EfDocRef } from '../composables/incorporationEfilingModel';
import type { EfUploadedFile } from '../composables/useIncorporationEfiling';

const props = withDefaults(
  defineProps<{
    docs: EfDocRef[];
    maxCount?: number;
    readonly?: boolean;
    uploadedFiles?: EfUploadedFile[];
    label?: string;
    hint?: string;
  }>(),
  { maxCount: 2, readonly: false, uploadedFiles: () => [], label: '', hint: '' },
);

const fileInput = ref<HTMLInputElement | null>(null);
const error = ref('');
const busy = ref(false);
/** null = append mode; number = replace the doc at that index. */
const pendingIndex = ref<number | null>(null);

function resolved(d: EfDocRef): { name: string; url: string; isNew: boolean } {
  if (d.contentBase64) {
    return { name: d.documentFileName || 'New document', url: '', isNew: true };
  }
  const match = props.uploadedFiles.find((f) => f.fileCd === d.fileCd);
  return {
    name: d.documentFileName || match?.fileName || String(d.fileCd || ''),
    url: match?.url || '',
    isNew: false,
  };
}

function sizeLabel(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function pickAdd() {
  error.value = '';
  pendingIndex.value = null;
  fileInput.value?.click();
}

function pickReplace(i: number) {
  error.value = '';
  pendingIndex.value = i;
  fileInput.value?.click();
}

async function onFile(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  const target = pendingIndex.value;
  if (!file) {
    pendingIndex.value = null;
    return;
  }
  if (target === null && props.docs.length >= props.maxCount) {
    error.value = `Up to ${props.maxCount} document(s) allowed.`;
    input.value = '';
    return;
  }
  const msg = validateEfUploadFile(file);
  if (msg) {
    error.value = msg;
    input.value = '';
    pendingIndex.value = null;
    return;
  }
  busy.value = true;
  try {
    const docRef = await makeDocRefFromFile(file);
    if (target === null) {
      props.docs.push(docRef);
    } else {
      const old = props.docs[target];
      if (old?.fileCd) docRef.replacesFileCd = old.fileCd;
      props.docs.splice(target, 1, docRef);
    }
  } catch (err: any) {
    error.value = err?.message || 'Failed to read file.';
  } finally {
    busy.value = false;
    pendingIndex.value = null;
    input.value = '';
  }
}

function remove(i: number) {
  props.docs.splice(i, 1);
}
</script>

<template>
  <div class="ef-upload">
    <div v-if="label" class="ef-upload__label">{{ label }}</div>

    <div v-for="(d, i) in docs" :key="i" class="ef-upload__row">
      <v-icon size="16" :color="resolved(d).isNew ? 'teal' : 'primary'">
        mdi-file-document-outline
      </v-icon>
      <a
        v-if="resolved(d).url"
        :href="resolved(d).url"
        target="_blank"
        rel="noopener"
        class="ef-upload__name ef-upload__name--link"
      >
        {{ resolved(d).name }}
      </a>
      <span v-else class="ef-upload__name">{{ resolved(d).name }}</span>
      <v-chip v-if="resolved(d).isNew" size="x-small" color="teal" variant="tonal" class="ml-1">
        new<template v-if="sizeLabel(d.size)"> · {{ sizeLabel(d.size) }}</template>
      </v-chip>
      <v-chip
        v-else-if="d.replacesFileCd"
        size="x-small"
        color="orange"
        variant="tonal"
        class="ml-1"
      >
        replaced
      </v-chip>
      <v-spacer />
      <v-btn
        v-if="!readonly"
        variant="text"
        size="x-small"
        class="text-none"
        prepend-icon="mdi-swap-horizontal"
        :loading="busy && pendingIndex === i"
        @click="pickReplace(i)"
      >
        Replace
      </v-btn>
      <v-btn
        v-if="!readonly"
        icon="mdi-close"
        size="x-small"
        variant="text"
        @click="remove(i)"
      />
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="application/pdf"
      class="ef-upload__input"
      @change="onFile"
    />

    <div v-if="!readonly && docs.length < maxCount" class="ef-upload__add">
      <v-btn
        variant="outlined"
        color="primary"
        size="small"
        class="text-none"
        prepend-icon="mdi-paperclip"
        :loading="busy && pendingIndex === null"
        @click="pickAdd"
      >
        Attach PDF
      </v-btn>
      <span v-if="hint" class="ef-upload__hint">{{ hint }}</span>
    </div>

    <div v-if="error" class="ef-upload__error">{{ error }}</div>
  </div>
</template>

<style scoped>
.ef-upload__label {
  font-size: 12.5px;
  font-weight: 700;
  color: #42506a;
  margin-bottom: 6px;
}
.ef-upload__row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  margin-bottom: 6px;
}
.ef-upload__name {
  font-size: 12.5px;
  font-weight: 600;
  color: #1a2336;
}
.ef-upload__name--link {
  color: #2563eb;
  text-decoration: none;
}
.ef-upload__add {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ef-upload__input {
  display: none;
}
.ef-upload__hint {
  font-size: 12px;
  color: #8a95a8;
}
.ef-upload__error {
  font-size: 12px;
  color: #c0392b;
  margin-top: 4px;
}
</style>
